import { useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'
import { OverlayPortal } from './ModalPortal'
import { loadPdfjs } from '../utils/pdfjs'
import { OrellaSpinner } from './OrellaLoader' // s123: the app's one spinner

/**
 * StoredDocViewer — s101 Phase 3 (TestFlight b65 x_19, Alex: "View document doesn't work").
 *
 * ★ THE OLD BUTTON WAS AN <a href target="_blank"> TO doc.url. Two ways to go nowhere: in the
 *   iOS web view a _blank anchor does not open, and doc.url is a link signed when the case was
 *   read, dead an hour later. This asks the API for a FRESH link from the stored key (with the
 *   case named, so the patient is admitted) and shows the file INSIDE the app: a PDF as page
 *   canvases (the Case Report viewer's pdf.js, same reason: WKWebView shows one page of a PDF in
 *   an iframe), an image as an image. One close button, no save or share.
 */
const isStorageKey = (v) => typeof v === 'string' && v && !/^(https?:|data:|blob:)/.test(v)
export const storedDocKey = (doc) =>
  (doc && [doc.storagePath, doc.path, doc.url].find(isStorageKey)) || null
export const storedDocIsPdf = (doc) => {
  const type = String((doc && (doc.type || doc.mimeType)) || '').toLowerCase()
  if (type.includes('pdf')) return true
  if (type.startsWith('image/')) return false
  const name = String((doc && (doc.name || storedDocKey(doc) || doc.url)) || '').toLowerCase().split('?')[0]
  return name.endsWith('.pdf')
}

export default function StoredDocViewer({ open, doc, caseId, title, onClose, t }) {
  const [phase, setPhase] = useState('loading') // loading | ready | error
  const [imgSrc, setImgSrc] = useState(null)
  const [pageCount, setPageCount] = useState(0)
  const [attempt, setAttempt] = useState(0)
  const pagesRef = useRef(null)
  const tr = (k, d, o) => (t ? t(k, { defaultValue: d, ...(o || {}) }) : d)
  const pdf = storedDocIsPdf(doc)

  useEffect(() => {
    if (!open || !doc) return undefined
    let cancelled = false
    let pdfDoc = null
    setPhase('loading'); setImgSrc(null); setPageCount(0)
    ;(async () => {
      try {
        const key = storedDocKey(doc)
        // Imported when the viewer opens, so a screen that only mounts it pulls in no API client.
        const { filesAPI } = key ? await import('../services/api') : {}
        const url = key ? await filesAPI.signedUrlForCase(key, caseId) : (doc.url || null)
        if (!url) throw new Error('no link')
        if (!pdf) {
          if (cancelled) return
          setImgSrc(url); setPhase('ready'); return
        }
        const [lib, res] = await Promise.all([loadPdfjs(), fetch(url)])
        if (!res.ok) throw new Error('fetch ' + res.status)
        const data = new Uint8Array(await res.arrayBuffer())
        pdfDoc = await lib.getDocument({ data }).promise
        if (cancelled) return
        setPageCount(pdfDoc.numPages)
        setPhase('ready')
        await new Promise((r) => requestAnimationFrame(() => r()))
        const host = pagesRef.current
        if (!host) return
        const cssWidth = Math.max(260, host.clientWidth - 28)
        const dpr = Math.min(window.devicePixelRatio || 1, 3)
        for (let n = 1; n <= pdfDoc.numPages; n++) {
          if (cancelled) return
          const page = await pdfDoc.getPage(n)
          const base = page.getViewport({ scale: 1 })
          const vp = page.getViewport({ scale: (cssWidth / base.width) * dpr })
          const canvas = host.querySelector(`canvas[data-page="${n}"]`)
          if (!canvas) continue
          canvas.width = Math.floor(vp.width)
          canvas.height = Math.floor(vp.height)
          canvas.style.width = cssWidth + 'px'
          canvas.style.height = Math.floor(vp.height / dpr) + 'px'
          await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise
        }
      } catch (e) {
        if (!cancelled) setPhase('error')
      }
    })()
    return () => { cancelled = true; if (pdfDoc) { try { pdfDoc.destroy() } catch (e) { /* closed */ } } }
  }, [open, doc, caseId, attempt, pdf])

  if (!open || !doc) return null
  const NAVY = '#15354F'
  return (
    <OverlayPortal>
      <div className="fixed inset-0 z-[10000] flex flex-col" style={{ background: '#5F6B76' }}
           data-testid="stored-doc-viewer" onContextMenu={(e) => e.preventDefault()}>
        <div className="flex items-center justify-between px-4 pb-3 flex-shrink-0"
             style={{ background: NAVY, paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}>
          <div className="min-w-0">
            <p className="m-0 text-white text-[16px] font-bold leading-tight truncate">{title || doc.name || tr('docViewer.title', 'Document')}</p>
            <p className="m-0 text-[11.5px] font-medium" style={{ color: '#9CB3C6' }}>{tr('reportViewer.viewOnly', 'View only')}</p>
          </div>
          <button type="button" onClick={onClose} aria-label={tr('common.close', 'Close')} data-cta="stored-doc-close"
                  className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,255,255,.14)' }}>
            <X size={18} color="#fff" />
          </button>
        </div>
        <div ref={pagesRef} className="flex-1 overflow-y-auto px-3.5 py-4 flex flex-col items-center gap-2" style={{ WebkitUserSelect: 'none', userSelect: 'none' }}>
          {phase === 'loading' && (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 text-white text-[13.5px]">
              <OrellaSpinner size={26} />
              {tr('docViewer.loading', 'Opening the document…')}
            </div>
          )}
          {phase === 'error' && (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center px-6">
              <p className="m-0 text-white text-[14px] leading-snug">{tr('docViewer.failed', "We couldn't open this document. Please try again.")}</p>
              <button type="button" onClick={() => setAttempt((a) => a + 1)}
                      className="h-10 px-5 rounded-full bg-white text-[13.5px] font-bold" style={{ color: NAVY }}>
                {tr('reportViewer.retry', 'Try again')}
              </button>
            </div>
          )}
          {phase === 'ready' && !pdf && imgSrc && (
            <img src={imgSrc} alt={title || doc.name || ''} className="bg-white rounded-[3px] max-w-full" draggable={false}
                 onError={() => setPhase('error')} style={{ boxShadow: '0 3px 10px rgba(0,0,0,.25)' }} />
          )}
          {phase === 'ready' && pdf && Array.from({ length: pageCount }, (_, i) => (
            <div key={i} className="flex flex-col items-center gap-1.5">
              <canvas data-page={i + 1} className="bg-white rounded-[3px]" style={{ boxShadow: '0 3px 10px rgba(0,0,0,.25)', maxWidth: '100%' }} />
              <span className="text-[11px] font-semibold mb-2" style={{ color: '#E5ECF2' }}>
                {tr('reportViewer.page', 'Page {{n}} of {{total}}', { n: i + 1, total: pageCount })}
              </span>
            </div>
          ))}
        </div>
      </div>
    </OverlayPortal>
  )
}
